// ============================================================================
// INTAKE VOLUME — is work arriving at all.
// ----------------------------------------------------------------------------
// The only genuine time series on this board. `created_at` is real and well
// spread; `updated_at` is a Dexie cache-write stamp that reads "today" for every
// row, so nothing here may touch it. DerivedRow.createdMs already carries
// created_at and nothing else, and DerivedRow.value already carries the ex GST
// figure computed once on the summary object, so this file only buckets and
// sums what the model handed it.
// ============================================================================

import type { AnalysisContext, Cell, DerivedRow, PanelDef, PanelSeries } from '../registry';
import { censusPhrase } from '../model';
import { money } from '../model';

const MONTHS_SHOWN = 12;

/**
 * Written out rather than taken from Intl: `en-AU` abbreviates to Sept, June and
 * July, which breaks the spec's `12 months to Jul 2026` stamp and gives the
 * twelve column labels three different widths in a 26px track.
 */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface MonthSlot {
    /** ISO-ish 'YYYY-MM'. Sorts correctly as text, which the CSV relies on. */
    key: string;
    /** Drawn under the column. */
    short: string;
    /** Read as a sentence in the formula, the stamp and the drawer title. */
    full: string;
    startMs: number;
    endMs: number;
}

function monthSlot(year: number, monthIndex: number): MonthSlot {
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 1);
    const abbr = MONTH_ABBR[start.getMonth()] ?? '';
    return {
        key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
        short: abbr,
        full: `${abbr} ${start.getFullYear()}`,
        startMs: start.getTime(),
        endMs: end.getTime(),
    };
}

/**
 * The twelve slots ending at the latest month that actually holds a job.
 * Anchoring on the latest row rather than on `now` keeps the last column
 * populated: a book that stopped three months ago should show its own last
 * month at the right edge, not three empty columns after it.
 */
function buildSlots(rows: DerivedRow[]): MonthSlot[] {
    let latest = 0;
    for (const r of rows) {
        if (r.createdMs > 0 && r.createdMs > latest) latest = r.createdMs;
    }
    if (!latest) return [];
    const anchor = new Date(latest);
    const year = anchor.getFullYear();
    const monthIndex = anchor.getMonth();
    const slots: MonthSlot[] = [];
    for (let back = MONTHS_SHOWN - 1; back >= 0; back -= 1) {
        slots.push(monthSlot(year, monthIndex - back));
    }
    return slots;
}

function inSlot(r: DerivedRow, slot: MonthSlot): boolean {
    return r.createdMs >= slot.startMs && r.createdMs < slot.endMs;
}

function sumValue(rows: DerivedRow[]): number {
    let total = 0;
    for (const r of rows) total += r.value;
    return total;
}

/** The label reaches the print document unparsed, so it is escaped at the edge. */
function esc(s: string): string {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function monthCell(slot: MonthSlot, rows: DerivedRow[], maxCount: number): Cell {
    const picked = rows.filter(r => inSlot(r, slot));
    return {
        key: slot.key,
        label: slot.short,
        count: picked.length,
        value: sumValue(picked),
        weight: maxCount > 0 ? picked.length / maxCount : 0,
        // THE PROOF. Pure over the rows handed in, so it reproduces its own count.
        select(source: DerivedRow[]): DerivedRow[] {
            return source.filter(r => inSlot(r, slot));
        },
        // THE WORKING. Described over what select() returned, never over the
        // closure, so a drawer opened on a narrower population still tells the truth.
        formula(chosen: DerivedRow[]): string {
            if (!chosen.length) return `0 rows created in ${slot.full} · nothing arrived that month`;
            return `${chosen.length} rows created in ${slot.full} × sum(scope[].amount) ex GST = ${money(sumValue(chosen))}`;
        },
        // THE DOOR. A month is not expressible as a Run Sheet search term, so the
        // handoff opens the whole book rather than pretending to carry the filter.
        drill: {
            lane: 'all',
            query: '',
            sort: 'newest',
            exact: false,
            note: 'The run sheet cannot filter by month. This opens the full book, newest first. Use the CSV for one month on its own.',
        },
    };
}

const intakeVolume: PanelDef = {
    id: 'intake-volume',
    title: 'Intake volume',
    hint: 'new jobs arriving, by month',
    group: 'flow',
    defaultSpan: 6,
    minSpan: 6,
    slim: false,
    mark: 'bars',
    orientation: 'column',
    respectsPeriod: true,

    compute(ctx: AnalysisContext): PanelSeries {
        const rows = ctx.rows;
        const slots = buildSlots(rows);
        if (!slots.length) {
            return {
                cells: [],
                total: { count: 0, value: 0 },
                emptySeries: 'No job here carries a created date, so there is no timeline to draw.',
            };
        }

        // Counted once so every cell's weight shares one denominator.
        let maxCount = 0;
        for (const slot of slots) {
            let n = 0;
            for (const r of rows) if (inSlot(r, slot)) n += 1;
            if (n > maxCount) maxCount = n;
        }

        // A month with no jobs still gets a cell: a gap in intake is information,
        // and dropping the month would draw a continuous line that never happened.
        const cells = slots.map(slot => monthCell(slot, rows, maxCount));

        // `total` is the panel's POPULATION, not the sum of its marks. The two
        // readings diverge the moment a panel has marginal cells or a mark that
        // shows fewer rows than it counted, and the stamps in the spec all quote
        // the population. Defined the same way in all six panels.
        return {
            cells,
            total: {
                count: ctx.rows.length,
                value: ctx.rows.reduce((s, r) => s + r.value, 0),
            },
        };
    },

    readiness(s: PanelSeries, ctx: AnalysisContext): 'live' | 'thin' | 'dormant' {
        // An EMPTY WINDOW is not dormancy. dormant means the source does not
        // exist and a dashed border says "this will never work as things stand";
        // a filter that matched nothing is a shell-owned empty state that clears
        // the moment the operator widens the period. Conflating them painted
        // three panels as permanently dead on any over-narrow filter.
        if (ctx.rows.length === 0) return 'live';
        if (!s.cells.length) return 'dormant';
        const withJobs = s.cells.filter(c => c.count > 0).length;
        // Two months of arrivals is a pair of numbers, not a trend line.
        return withJobs < 3 ? 'thin' : 'live';
    },

    activation(s: PanelSeries): string | null {
        if (!s.cells.length) {
            return 'No job carries a created date, so no month can be counted. This fills in as soon as jobs arrive carrying a created date.';
        }
        const withJobs = s.cells.filter(c => c.count > 0).length;
        if (withJobs < 3) {
            const noun = withJobs === 1 ? 'month carries' : 'months carry';
            return `Only ${withJobs} of the last 12 ${noun} a job, so the shape is not yet a trend. It fills in as more jobs arrive or the period widens.`;
        }
        return null;
    },

    // THE SIGNATURE. `n` is the rows the bars are actually made of, which can be
    // fewer than the board's population when the period reaches further back than
    // the twelve columns drawn. The trailing clause names the window that cut it.
    stamp(s: PanelSeries, ctx: AnalysisContext): string {
        const census = censusPhrase(s.total.count, ctx.window.capped, '').trim();
        const last = s.cells.length ? s.cells[s.cells.length - 1] : undefined;
        if (!last) return `${census} · created_at · no dated jobs`;
        const slot = monthSlot(
            Number(last.key.slice(0, 4)),
            Number(last.key.slice(5, 7)) - 1,
        );
        return `${census} · created_at · 12 months to ${slot.full}`;
    },

    fields: ['createdAt', 'scope[].amount', 'scopeGstInclusive'],

    caveats: [
        'A job counts in the month it arrived, not the month it was quoted or sent.',
        'Jobs with no created date are not drawn.',
        'Months with no jobs are drawn as empty columns, because a gap in intake is information.',
    ],

    csv(s: PanelSeries) {
        return {
            headers: ['Month', 'Jobs', 'Value ex GST'],
            rows: s.cells.map(c => ({
                // The ISO-ish key, not the label, so a spreadsheet sorts the
                // months chronologically instead of alphabetically.
                Month: c.key,
                Jobs: c.count,
                'Value ex GST': Math.round(c.value),
            })),
        };
    },

    /**
     * Static SVG only. The print document is served script-src 'none', so every
     * coordinate is resolved here and nothing measures itself on paper.
     */
    printSvg(s: PanelSeries, w: number, h: number): string {
        const width = Math.max(1, Math.round(w));
        const height = Math.max(1, Math.round(h));
        const open = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="New jobs by month">`;

        const cells = s.cells;
        if (!cells.length) {
            return `${open}<title>Intake volume</title><text x="0" y="${Math.round(height / 2)}" font-family="Inter, system-ui, sans-serif" font-size="9" fill="currentColor" opacity="0.6">No dated jobs to draw.</text></svg>`;
        }

        const gap = 4;
        const countH = 10;
        const labelH = 11;
        const plotH = Math.max(8, height - countH - labelH - 6);
        const plotTop = countH + 3;
        const colW = Math.max(2, (width - gap * (cells.length - 1)) / cells.length);
        const barW = Math.min(colW, 26);

        let maxCount = 0;
        for (const c of cells) if (c.count > maxCount) maxCount = c.count;

        const parts: string[] = [`<title>Intake volume, ${esc(String(cells.length))} months</title>`];
        cells.forEach((c, i) => {
            const colX = i * (colW + gap);
            const cx = colX + colW / 2;
            const barX = colX + (colW - barW) / 2;
            const barH = c.count > 0 && maxCount > 0
                ? Math.max(2, Math.round(plotH * (c.count / maxCount)))
                : 0;
            const barY = plotTop + (plotH - barH);

            if (barH > 0) {
                parts.push(
                    `<rect x="${barX.toFixed(2)}" y="${barY.toFixed(2)}" width="${barW.toFixed(2)}" height="${barH}" rx="2" fill="var(--qm-accent, currentColor)"/>`,
                );
                parts.push(
                    `<text x="${cx.toFixed(2)}" y="${(barY - 3).toFixed(2)}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="8" font-weight="700" fill="currentColor">${c.count}</text>`,
                );
            } else {
                // An empty month is drawn, not skipped, so the gap stays visible.
                parts.push(
                    `<rect x="${barX.toFixed(2)}" y="${(plotTop + plotH - 2).toFixed(2)}" width="${barW.toFixed(2)}" height="2" fill="currentColor" opacity="0.22"/>`,
                );
            }

            parts.push(
                `<text x="${cx.toFixed(2)}" y="${height - 2}" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="8" fill="currentColor" opacity="0.65">${esc(c.label)}</text>`,
            );
        });

        return `${open}${parts.join('')}</svg>`;
    },
};

export default intakeVolume;
